<script setup lang="ts">
  import Alert from '@leaflink/stash/Alert.vue';
  import Divider from '@leaflink/stash/Divider.vue';
  import Expand from '@leaflink/stash/Expand.vue';
  import Icon from '@leaflink/stash/Icon.vue';
  import Input from '@leaflink/stash/Input.vue';
  import Module from '@leaflink/stash/Module.vue';
  import RadioGroup from '@leaflink/stash/RadioGroup.vue';
  import RadioNew from '@leaflink/stash/RadioNew.vue';
  import Table from '@leaflink/stash/Table.vue';
  import TableCell from '@leaflink/stash/TableCell.vue';
  import TableHeaderCell from '@leaflink/stash/TableHeaderCell.vue';
  import TableHeaderRow from '@leaflink/stash/TableHeaderRow.vue';
  import TableRow from '@leaflink/stash/TableRow.vue';
  import useModals from '@leaflink/stash/useModals';
  import { computed, ref } from 'vue';
  import { I18nT } from 'vue-i18n';

  import CategoryManagementInterestModal from '@/components/CategoryManagement/CategoryManagementInterestModal.vue';
  import { useFeatureEnrollmentProvider } from '@/components/FeatureEnrollment/FeatureEnrollmentLayout.vue';
  import LicenseTypeChip from '@/components/LicenseTypeChip/LicenseTypeChip.vue';
  import { type PosSystemName } from '@/models/inventoryInsights';
  import { useInventoryInsightsProvider } from '@/pages/FeatureEnrollment/InventoryInsights/InventoryInsights.vue';
  import i18n from '@/plugins/i18n';
  import { useValidatePOSIntegrationPost } from '@/queries/feature-enrollment/feature-enrollment.query';
  import { trackWithSegment } from '@/segment';
  import { type PostPOSIntegrationRequestData } from '@/services/api/models/settings';
  import formatAddress from '@/utils/formatAddress';

  import ConnectingPOSInfo from '../components/ConnectingPOSInfo.vue';

  const isExpanded = ref(false);
  const nextWhileValidating = ref(false);

  const modals = useModals();

  const { company, productName, selectedPosSystem, posCredentials, addPosStatus } = useInventoryInsightsProvider();

  const validatePOSIntegrationQuery = useValidatePOSIntegrationPost();

  const hasServerSideValidationError = computed(
    () =>
      validatePOSIntegrationQuery.isSuccess.value &&
      validatePOSIntegrationQuery.data.value?.verificationStatus?.includes('Invalid'),
  );

  const invalidCredentialsAlertMessage = computed(() => {
    switch (selectedPosSystem.value) {
      case 'flowhub':
        return i18n.t('inventoryInsights.invalidFlowhubInputMessage');
      case 'dutchie':
        return i18n.t('inventoryInsights.invalidDutchieInputMessage');
      default:
        return '';
    }
  });

  const areRequiredFieldsPresent = () => {
    if (selectedPosSystem.value === 'dutchie' && !posCredentials.dutchie.apiKey) {
      addPosStatus.validationError.dutchie.apiKey = i18n.t('inventoryInsights.enterAPIKey');
      return false;
    }

    if (
      selectedPosSystem.value === 'flowhub' &&
      !(posCredentials.flowhub.locationId && posCredentials.flowhub.clientId && posCredentials.flowhub.apiKey)
    ) {
      if (!posCredentials.flowhub.apiKey) {
        addPosStatus.validationError.flowhub.apiKey = i18n.t('inventoryInsights.enterAPIKey');
      }
      if (!posCredentials.flowhub.locationId) {
        addPosStatus.validationError.flowhub.locationId = i18n.t('inventoryInsights.enterLocationId');
      }
      if (!posCredentials.flowhub.clientId) {
        addPosStatus.validationError.flowhub.clientId = i18n.t('inventoryInsights.enterClientId');
      }

      return false;
    }

    return true;
  };

  const getSettings = (systemName: PosSystemName) => {
    const settings: Record<PosSystemName, PostPOSIntegrationRequestData['integrationSettings']> = {
      dutchie: [{ settingName: 'api_key', settingValue: posCredentials.dutchie.apiKey }],
      flowhub: [
        {
          settingName: 'api_key',
          settingValue: posCredentials.flowhub.apiKey,
        },
        {
          settingName: 'client_id',
          settingValue: posCredentials.flowhub.clientId,
        },
        { settingName: 'location_id', settingValue: posCredentials.flowhub.locationId },
      ],
    };

    return settings[systemName];
  };

  async function validatePOSIntegration() {
    // Check if required fields are present, show inline error if not
    if (!areRequiredFieldsPresent() || !selectedPosSystem.value || selectedPosSystem.value === 'other') {
      return;
    }

    validatePOSIntegrationQuery.reset();

    const body: PostPOSIntegrationRequestData = {
      integratedSystemName: selectedPosSystem.value,
      integrationSettings: getSettings(selectedPosSystem.value),
    };

    await validatePOSIntegrationQuery.mutateAsync(body);

    if (!validatePOSIntegrationQuery.isSuccess.value) {
      addPosStatus.isValid = false;

      if (selectedPosSystem.value === 'dutchie') {
        addPosStatus.validationError.dutchie.apiKey = i18n.t('inventoryInsights.invalidAPIKey');
      }

      if (selectedPosSystem.value === 'flowhub') {
        addPosStatus.validationError.flowhub.apiKey = i18n.t('inventoryInsights.invalidAPIKey');
        addPosStatus.validationError.flowhub.clientId = i18n.t('inventoryInsights.reenterClientId');
        addPosStatus.validationError.flowhub.locationId = i18n.t('inventoryInsights.reenterLocationId');
      }
    }

    if (hasServerSideValidationError.value) {
      addPosStatus.isValid = false;

      if (selectedPosSystem.value === 'dutchie') {
        posCredentials.dutchie.apiKey = '';
        addPosStatus.validationError.dutchie.apiKey = i18n.t('inventoryInsights.invalidAPIKey');
      }

      if (selectedPosSystem.value === 'flowhub') {
        posCredentials.flowhub.apiKey = '';
        posCredentials.flowhub.clientId = '';
        posCredentials.flowhub.locationId = '';
        addPosStatus.validationError.flowhub.apiKey = i18n.t('inventoryInsights.invalidAPIKey');
        addPosStatus.validationError.flowhub.clientId = i18n.t('inventoryInsights.reenterClientId');
        addPosStatus.validationError.flowhub.locationId = i18n.t('inventoryInsights.reenterLocationId');
      }
    }

    if (!hasServerSideValidationError.value) {
      addPosStatus.isValid = true;

      resetValidation();

      if (nextWhileValidating.value && stepperRef.value) {
        stepperRef.value.next();
      }
    }

    nextWhileValidating.value = false;

    trackWithSegment({
      event: hasServerSideValidationError.value
        ? 'inventoryInsightsEnrollmentPosInfoError'
        : 'inventoryInsightsEnrollmentPosInfoValidated',
      properties: {
        product_name: productName,
        element_engaged: 'API Key Input Field',
        page_engaged_from: currentStep.value?.text,
        pos_system: selectedPosSystem.value,
      },
    });
  }

  const { isProcessing, currentStep, stepperRef, isNavForwardDisabled } = useFeatureEnrollmentProvider({
    events: {
      beforeNext() {
        if (!selectedPosSystem.value) {
          throw new Error('POS System is required');
        }

        // Throw error if API key validation process is underway
        if (validatePOSIntegrationQuery.isPending.value) {
          isProcessing.value = true;
          nextWhileValidating.value = true;
          throw new Error('Credentials validation in progress');
        }

        resetValidation();

        if (selectedPosSystem.value === 'dutchie' && !posCredentials.dutchie.apiKey) {
          addPosStatus.validationError.dutchie.apiKey = i18n.t('inventoryInsights.enterAPIKey');
          throw new Error('API Key field is required');
        }

        if (
          selectedPosSystem.value === 'flowhub' &&
          (!posCredentials.flowhub.locationId || !posCredentials.flowhub.clientId || !posCredentials.flowhub.apiKey)
        ) {
          if (!posCredentials.flowhub.apiKey) {
            addPosStatus.validationError.flowhub.apiKey = i18n.t('inventoryInsights.enterAPIKey');
          }
          if (!posCredentials.flowhub.locationId) {
            addPosStatus.validationError.flowhub.locationId = i18n.t('inventoryInsights.enterLocationId');
          }
          if (!posCredentials.flowhub.clientId) {
            addPosStatus.validationError.flowhub.clientId = i18n.t('inventoryInsights.enterClientId');
          }

          throw new Error('Location ID and Client ID fields are required');
        }

        // Throw error if it has validation errors
        if (
          addPosStatus.validationError.dutchie.apiKey ||
          addPosStatus.validationError.flowhub.apiKey ||
          addPosStatus.validationError.flowhub.clientId ||
          addPosStatus.validationError.flowhub.locationId
        ) {
          throw new Error('Invalid credentials entry');
        }
      },
    },
  });

  const handleAccordionToggle = () => {
    isExpanded.value = !isExpanded.value;

    if (!isExpanded.value) return;

    trackWithSegment({
      event: 'inventoryInsightsEnrollmentInfoClicked',
      properties: {
        pos_system: selectedPosSystem.value,
        product_name: productName,
        element_engaged: 'How do I link API Key Drawer',
        page_engaged_from: currentStep.value?.text || '',
      },
    });
  };

  const handleJoiningWaitlist = () => {
    //open modal
    modals.open({
      component: CategoryManagementInterestModal,
      name: 'CategoryManagementInterestModal',
      attributes: {
        id: 1,
        isFlexible: false,
        source: 'inventory-insights-enrollment',
        close: () => modals.close(),
      },
    });

    trackWithSegment({
      event: 'inventoryInsightsEnrollmentWaitlistClicked',
      properties: {
        product_name: productName,
        element_engaged: 'Join the Waitlist link',
        page_engaged_from: currentStep.value?.text,
      },
    });
  };

  const resetValidation = () => {
    addPosStatus.validationError.dutchie.apiKey = '';
    addPosStatus.validationError.flowhub.apiKey = '';
    addPosStatus.validationError.flowhub.clientId = '';
    addPosStatus.validationError.flowhub.locationId = '';
  };

  function handlePosChange(newSelectedPosSystem: string) {
    resetValidation();
    validatePOSIntegrationQuery.reset();

    isNavForwardDisabled.value = newSelectedPosSystem === 'other';
  }

  function handleFlowhubBlur() {
    if (posCredentials.flowhub.locationId && posCredentials.flowhub.clientId && posCredentials.flowhub.apiKey) {
      validatePOSIntegration();
    }
  }
</script>

<template>
  <div class="tw-w-full tw-text-ice-900 tw-flex-1 tw-bg-white">
    <h3 class="tw-mb-2">{{ i18n.t('inventoryInsights.supportedPOSProvider') }}</h3>
    <Divider class="tw-mb-4" />
    <div class="tw-flex tw-items-center tw-mb-8">
      <RadioGroup v-model="selectedPosSystem" variant="chip" @update:model-value="handlePosChange">
        <RadioNew id="dutchie" label="Dutchie" value="dutchie" />
        <RadioNew id="flowhub" label="Flowhub" value="flowhub" />
        <RadioNew id="other" label="Other" value="other" />
      </RadioGroup>
    </div>

    <div v-if="selectedPosSystem !== 'other'" class="tw-flex tw-flex-col">
      <h3 class="tw-pb-3">
        {{
          i18n.t(
            selectedPosSystem === 'dutchie' ? 'inventoryInsights.addAPIKey' : 'inventoryInsights.addFlowhubCredentials',
          )
        }}
      </h3>
      <div v-if="hasServerSideValidationError && invalidCredentialsAlertMessage" class="tw-flex-1 tw-mb-6">
        <Alert centered class="!tw-my-0" severity="error" permanent>{{ invalidCredentialsAlertMessage }}</Alert>
      </div>
      <Table class="tw-mb-4 tw-border tw-border-ice-500">
        <template #head>
          <TableHeaderRow>
            <TableHeaderCell>
              {{ i18n.t('inventoryInsights.locationDetails') }}
            </TableHeaderCell>

            <TableHeaderCell>
              {{ i18n.t('inventoryInsights.license') }}
            </TableHeaderCell>

            <TableHeaderCell v-if="selectedPosSystem === 'dutchie'">
              {{ i18n.t('inventoryInsights.apiKey') }}
            </TableHeaderCell>

            <template v-if="selectedPosSystem === 'flowhub'">
              <TableHeaderCell>
                {{ i18n.t('inventoryInsights.locationId') }}
              </TableHeaderCell>

              <TableHeaderCell>
                {{ i18n.t('inventoryInsights.clientId') }}
              </TableHeaderCell>

              <TableHeaderCell>
                {{ i18n.t('inventoryInsights.apiKey') }}
              </TableHeaderCell>
            </template>
          </TableHeaderRow>
        </template>
        <template #body>
          <TableRow>
            <TableCell class="!tw-py-[42px]">
              <div class="tw-flex-col tw-items-center !tw-py-0">
                <p class="tw-mb-0">{{ company.name }}</p>
                <p class="tw-text-xs tw-text-ice-700 tw-m-0">
                  {{ company.address ? formatAddress({ company: company.address }) : '' }}
                </p>
              </div>
            </TableCell>
            <TableCell class="!tw-py-[42px]">
              <div class="tw-flex tw-items-center">
                <div class="tw-flex tw-pr-1.5">
                  <LicenseTypeChip :license="{ type: company.license?.type || '' }" />
                </div>
                <div class="tw-flex-wrap tw-items-center">
                  <p class="tw-m-0">{{ company.license?.number }}</p>
                </div>
              </div>
            </TableCell>
            <TableCell class="!tw-py-[42px]">
              <Input
                v-if="selectedPosSystem === 'dutchie'"
                v-model="posCredentials.dutchie.apiKey"
                class="tw-relative [&>.stash-field-error]:tw-absolute"
                data-test="input|api-key"
                :error-text="addPosStatus.validationError.dutchie.apiKey"
                @blur="validatePOSIntegration"
              >
                <template #append>
                  <Icon
                    v-if="validatePOSIntegrationQuery.isPending.value"
                    class="fx-spin tw-text-ice-500"
                    data-test="icon|input-icon"
                    name="loading-empty"
                    size="dense"
                  />
                  <Icon
                    v-if="
                      !addPosStatus.validationError.dutchie.apiKey &&
                      posCredentials.dutchie.apiKey &&
                      !validatePOSIntegrationQuery.isPending.value &&
                      addPosStatus.isValid
                    "
                    class="tw-text-green-500"
                    data-test="icon|input-icon"
                    name="circle-check"
                    dense
                  />
                </template>
              </Input>
              <Input
                v-if="selectedPosSystem === 'flowhub'"
                v-model="posCredentials.flowhub.locationId"
                class="tw-relative [&>.stash-field-error]:tw-absolute"
                data-test="input|location-id"
                :error-text="addPosStatus.validationError.flowhub.locationId"
                @blur="handleFlowhubBlur"
              >
                <template #append>
                  <Icon
                    v-if="validatePOSIntegrationQuery.isPending.value"
                    class="fx-spin tw-text-ice-500"
                    data-test="icon|input-icon"
                    name="loading-empty"
                    size="dense"
                  />
                  <Icon
                    v-if="
                      !addPosStatus.validationError.flowhub.locationId &&
                      posCredentials.flowhub.apiKey &&
                      posCredentials.flowhub.clientId &&
                      posCredentials.flowhub.locationId &&
                      !validatePOSIntegrationQuery.isPending.value &&
                      addPosStatus.isValid
                    "
                    class="tw-text-green-500"
                    data-test="icon|input-icon"
                    name="circle-check"
                    dense
                  />
                </template>
              </Input>
            </TableCell>
            <TableCell v-if="selectedPosSystem === 'flowhub'" class="!tw-py-[42px]">
              <Input
                v-model="posCredentials.flowhub.clientId"
                class="tw-relative [&>.stash-field-error]:tw-absolute"
                data-test="input|client-id"
                :error-text="addPosStatus.validationError.flowhub.clientId"
                @blur="handleFlowhubBlur"
              >
                <template #append>
                  <Icon
                    v-if="validatePOSIntegrationQuery.isPending.value"
                    class="fx-spin tw-text-ice-500"
                    data-test="icon|input-icon"
                    name="loading-empty"
                    size="dense"
                  />
                  <Icon
                    v-if="
                      !addPosStatus.validationError.flowhub.clientId &&
                      posCredentials.flowhub.apiKey &&
                      posCredentials.flowhub.clientId &&
                      posCredentials.flowhub.locationId &&
                      !validatePOSIntegrationQuery.isPending.value &&
                      addPosStatus.isValid
                    "
                    class="tw-text-green-500"
                    data-test="icon|input-icon"
                    name="circle-check"
                    dense
                  />
                </template>
              </Input>
            </TableCell>
            <TableCell v-if="selectedPosSystem === 'flowhub'" class="!tw-py-[42px]">
              <Input
                v-model="posCredentials.flowhub.apiKey"
                class="tw-relative [&>.stash-field-error]:tw-absolute"
                data-test="input|api-key"
                :error-text="addPosStatus.validationError.flowhub.apiKey"
                @blur="handleFlowhubBlur"
              >
                <template #append>
                  <Icon
                    v-if="validatePOSIntegrationQuery.isPending.value"
                    class="fx-spin tw-text-ice-500"
                    data-test="icon|input-icon"
                    name="loading-empty"
                    size="dense"
                  />
                  <Icon
                    v-if="
                      !addPosStatus.validationError.flowhub.apiKey &&
                      posCredentials.flowhub.apiKey &&
                      posCredentials.flowhub.clientId &&
                      posCredentials.flowhub.locationId &&
                      !validatePOSIntegrationQuery.isPending.value &&
                      addPosStatus.isValid
                    "
                    class="tw-text-green-500"
                    data-test="icon|input-icon"
                    name="circle-check"
                    dense
                  />
                </template>
              </Input>
            </TableCell>
          </TableRow>
        </template>
      </Table>

      <Module class="tw-border tw-border-ice-500">
        <div
          class="tw-flex tw-items-center tw-justify-between tw-cursor-pointer"
          data-test="expand|toggle-expand"
          @click="handleAccordionToggle"
        >
          <div class="tw-flex">
            <Icon name="circle-info" class="tw-text-blue-500" />
            <h4 class="tw-mt-1 tw-pl-[6px]">
              {{
                i18n.t(
                  selectedPosSystem === 'dutchie'
                    ? 'inventoryInsights.findAPIKey'
                    : 'inventoryInsights.findFlowhubCredentials',
                )
              }}
            </h4>
          </div>

          <div>
            <Icon v-if="isExpanded" name="chevron-up" />
            <Icon v-else name="chevron-down" />
          </div>
        </div>

        <Expand :is-expanded="isExpanded">
          <Divider class="tw-mb-4 tw-mt-[18px]" />
          <ConnectingPOSInfo data-test="expand|expanded-content" />
        </Expand>
      </Module>
    </div>

    <I18nT
      v-else
      keypath="inventoryInsights.notSeeingPOS.description"
      scope="global"
      tag="span"
      class="tw-text-ice-700"
    >
      <template #link>
        <a href="#" class="tw-text-blue-500 tw-underline" @click.prevent="handleJoiningWaitlist">
          {{ i18n.t('inventoryInsights.notSeeingPOS.linkLabel') }}
        </a>
      </template>
    </I18nT>
  </div>
</template>
